import { useState, useEffect, useCallback, useRef } from "react";
import { collection, query, where, orderBy, limit, startAfter, getDocs, getDoc, addDoc, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { logAudit } from "../utils/auditTrail";
import { sanitizeObject } from "../utils/sanitize";

const PAGE_SIZE = 20;

export default function usePatients(doctorId) {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const lastDoc = useRef(null);

  const loadPatients = useCallback(async (append = false) => {
    if (!doctorId) return;
    setLoading(true);
    setError(null);
    try {
      let q = query(
        collection(db, "patients"),
        where("doctorId", "==", doctorId),
        where("activo", "==", true),
        orderBy("primerApellido"),
        limit(PAGE_SIZE)
      );
      if (append && lastDoc.current) {
        q = query(
          collection(db, "patients"),
          where("doctorId", "==", doctorId),
          where("activo", "==", true),
          orderBy("primerApellido"),
          startAfter(lastDoc.current),
          limit(PAGE_SIZE)
        );
      }
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      lastDoc.current = snap.docs[snap.docs.length - 1] || null;
      setHasMore(snap.docs.length === PAGE_SIZE);
      setPatients(prev => append ? [...prev, ...docs] : docs);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [doctorId]);

  useEffect(() => {
    lastDoc.current = null;
    loadPatients(false);
  }, [loadPatients]);

  const loadMore = useCallback(() => {
    if (hasMore && !loading) loadPatients(true);
  }, [hasMore, loading, loadPatients]);

  const searchPatients = useCallback(async (term) => {
    if (!doctorId || !term || !term.trim()) {
      lastDoc.current = null;
      return loadPatients(false);
    }
    setLoading(true);
    setError(null);
    try {
      const t = term.trim();
      let docs = [];
      if (/^\d+$/.test(t)) {
        const q1 = query(
          collection(db, "patients"),
          where("doctorId", "==", doctorId),
          where("activo", "==", true),
          where("numeroDocumento", "==", t)
        );
        const snap = await getDocs(q1);
        docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      } else {
        const upper = t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
        const end = upper.slice(0, -1) + String.fromCharCode(upper.charCodeAt(upper.length - 1) + 1);
        const q1 = query(
          collection(db, "patients"),
          where("doctorId", "==", doctorId),
          where("activo", "==", true),
          where("primerApellido", ">=", upper),
          where("primerApellido", "<", end),
          orderBy("primerApellido"),
          limit(PAGE_SIZE)
        );
        const snap = await getDocs(q1);
        docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      }
      setPatients(docs);
      setHasMore(false);
      lastDoc.current = null;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [doctorId, loadPatients]);

  const addPatient = useCallback(async (data) => {
    const ref = await addDoc(collection(db, "patients"), {
      ...sanitizeObject(data),
      doctorId,
      activo: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Vincular con cuenta de usuario si el paciente ya tiene cuenta registrada
    if (data.email) {
      try {
        const usersSnap = await getDocs(
          query(collection(db, "users"), where("email", "==", data.email), where("role", "==", "patient"))
        );
        if (!usersSnap.empty) {
          await updateDoc(ref, { linkedUserId: usersSnap.docs[0].id });
        }
      } catch (e) {
        // No bloquear si la vinculación falla (no crítico)
        console.warn("No se pudo vincular linkedUserId:", e.message);
      }
    }

    const snap = await getDoc(ref);
    const newP = { id: snap.id, ...snap.data() };
    setPatients(prev => [newP, ...prev]);
    await logAudit("crear_paciente", "patient", snap.id, doctorId, data.email || "", `${data.primerNombre} ${data.primerApellido}`);
    return newP;
  }, [doctorId]);

  const updatePatient = useCallback(async (id, data) => {
    const ref = doc(db, "patients", id);
    await updateDoc(ref, { ...sanitizeObject(data), updatedAt: serverTimestamp() });
    setPatients(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));
    await logAudit("actualizar_paciente", "patient", id, doctorId, "", `Campos: ${Object.keys(data).join(", ")}`);
  }, [doctorId]);

  const getPatient = useCallback(async (id) => {
    const snap = await getDoc(doc(db, "patients", id));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }, []);

  return { patients, loading, error, hasMore, searchPatients, addPatient, updatePatient, getPatient, loadMore };
}
